import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Checkbox,
  InputLabel,
  TextField,
  Typography,
  Link,
  Alert,
  Stack,
  Select,
  MenuItem,
  Radio,
  FormLabel,
  FormControlLabel,
  FormGroup,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { FilePicker } from 'react-file-picker';
import { useLocation, useParams } from 'react-router-dom';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// FORMATTED MESSAGES
import BulkSendFormattedMessages from '../../messages/websms/BulkSendFormattedMessages';

// UTILS
import { validatePhoneNumber, stripNonNumerical } from '../../utils/validate';
import {
  withinCharacterLimit,
  getMessageParts,
} from '../../utils/characterCount';

// NETWORK
import useBulkSendRequest from '../../requests/BulkSendRequest';

// COMPONENTS
import Layout from '../../components/layouts/Layout';
import BulkSendGuide from '../../components/websms/bulk/BulkSendGuide';
import SectionWrapper from '../../components/common/SectionWrapper';
import SideNote from '../../components/common/SideNote';
import CustomModal from '../../components/common/CustomModal';
import TemplateList from '../../components/templates/TemplateList';
import MessagesIcon from '../../components/icons/MessagesIcon';
import CreditsIcon from '../../components/icons/CreditsIcon';
import BulkSendTable from '../../components/websms/bulk/BulkSendTable';
import StagedModal from '../../components/common/StagedModal';

export default function BulkSend({
  customer,
  user,
  dateFormats,
  errorHandler,
}) {
  const [allowScheduling, setAllowScheduling] = useState(false);
  const [scheduledTime, setScheduledTime] = useState(new Date());
  const [subject, setSubject] = useState('');
  const [stage, setStage] = useState(0);
  const [sendModalDisplay, setSendModalDisplay] = useState(false);
  const [templateModalDisplay, setTemplateModalDisplay] = useState(false);
  const [messageIn, setMessageIn] = useState('C');
  const [template, setTemplate] = useState({ template_text: '' });
  const [csvFileObject, setCsvFileObject] = useState({
    name: 'Select a file to begin...',
  });
  const [fileSelected, setFileSelected] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [showInvalidRows, setShowInvalidRows] = useState(false);
  const [showRows, setShowRows] = useState('all');
  const [totalRows, setTotalRows] = useState([]);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [numberColumn, setNumberColumn] = useState('');
  const [nameColumn, setNameColumn] = useState('');
  const [messageColumn, setMessageColumn] = useState('');
  const [accountStatus, setAccountStatus] = useState('Loading...');
  const [isDeleting, setIsDeleting] = useState(false);
  const [headerRow, setHeaderRow] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [countryCode, setCountryCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [rowsToBeDeleted, setRowsToBeDeleted] = useState([]);
  const [openLandingMessage, setOpenLandingMessage] = useState(
    localStorage.getItem('showLandingMessage') === 'false' ? false : true
  );
  const theme = useTheme();
  const location = useLocation();
  const { getUser, update, sendBulkSMS, uploadCSV } = useBulkSendRequest();

  const handleLoadUserData = useCallback(async () => {
    try {
      const { user } = await getUser(errorHandler);
      setCountryCode(user.countryCode?.country_code);
      updateAccountStatus(user);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    handleLoadUserData();
  }, [handleLoadUserData]);

  useEffect(() => {
    if (location?.state?.landingMessage === true) setOpenLandingMessage(true);
  }, [location?.state?.landingMessage]);

  const handleHideLandingMessage = (options) => {
    if (options.hideMessage === true)
      localStorage.setItem('showLandingMessage', false);
    setOpenLandingMessage(false);
  };

  const clearCheckboxes = (name) => {
    const elements = document.getElementsByClassName(
      'form-check-input position-static'
    );
    for (let i = 0, len = elements.length; i < len; i++) {
      if (elements[i] && elements[i].name === name) {
        elements[i].checked = false;
      }
    }
  };

  const selectRowsToBeDeleted = (event) => {
    const target = event.target;
    let updatedRowsToBeDeleted = [...rowsToBeDeleted];

    if (target.checked) {
      updatedRowsToBeDeleted.push(event.target.id);
      setRowsToBeDeleted(updatedRowsToBeDeleted);
      setIsDeleting(true);
    } else {
      updatedRowsToBeDeleted = updatedRowsToBeDeleted.filter(
        (row) => row !== event.target.id
      );
      setRowsToBeDeleted(updatedRowsToBeDeleted);
      if (updatedRowsToBeDeleted.length === 0) {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteRows = () => {
    const updatedTotalRows = totalRows.filter(
      (currentRow) => !rowsToBeDeleted.includes(`recipient_${currentRow[0]}`)
    );

    clearCheckboxes('delete');
    setRowsToBeDeleted([]);
    setTotalRows(updatedTotalRows);
    setTotalRowsCount(updatedTotalRows.length);
    setIsDeleting(false);
    setPageNumber(1);
  };

  const changeScheduledDateTime = (date) => {
    setScheduledTime(date);
    setHasError(date > Date.now() ? false : true);
    setErrorText(
      date > Date.now()
        ? ''
        : 'Date/Time invalid, please select a future Date/Time.'
    );
  };

  const handleModal = async (modalName) => {
    if (allowScheduling && scheduledTime < Date.now()) {
      if (modalName === 'sendModalDisplay') {
        setHasError(true);
        setErrorText('Date/Time invalid, please select a future Date/Time.');
        return;
      }
    }
    if (modalName === 'sendModalDisplay') {
      setSendModalDisplay(!sendModalDisplay);
    } else if (modalName === 'templateModalDisplay') {
      setTemplateModalDisplay(!templateModalDisplay);
    }
  };

  const captureFile = async (file) => {
    try {
      const result = await uploadCSV(file, errorHandler);
      const headerRow = result.find((row) => row[0] === 1);

      setNumberColumn(1);
      setNameColumn(2);
      setMessageColumn(3);
      setMessageIn('C');
      setTemplate({ template_text: '' });
      setCsvFileObject(file);
      setFileSelected(true);
      setErrorMessage('');
      setHeaderRow(headerRow);
      setTotalRows(result || 'No data found');
      setTotalRowsCount(result?.length || 0);
      setPageNumber(1);
      setShowInvalidRows(false);
    } catch (error) {
      console.error(error);
    }
  };

  const updateAccountStatus = async (user) => {
    try {
      if (user) {
        const { customer_id, customer_user_id } = user;
        const { period, limit, messagesRemaining, creditRemaining } =
          (await update(customer_id, customer_user_id, errorHandler)) || {};
        setAccountStatus(
          `${period}/ ${limit}/ ${messagesRemaining}/ ${creditRemaining}`
        );
        return { creditRemaining, messagesRemaining };
      }
      return { creditRemaining: 0, messagesRemaining: 0 };
    } catch (error) {
      console.error(error);
    }
  };

  const handleFilterChange = (event) => {
    setShowRows(event.target.value);
    setPageNumber(1);
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (target.type === 'radio') {
      setPageNumber(1);
      // resetPaginator();
      clearCheckboxes('delete');
      setRowsToBeDeleted([]);
      setIsDeleting(false);
    }

    if (name === 'allowScheduling') {
      setAllowScheduling(value);
      if (
        value === false &&
        errorText === 'Date/Time invalid, please select a future Date/Time.'
      ) {
        setHasError(false);
      }
    } else if (name === 'numberColumn') {
      setNumberColumn(value);
    } else if (name === 'messageIn') {
      setMessageIn(value);
    } else if (name === 'messageColumn') {
      setMessageColumn(value);
    } else if (name === 'nameColumn') {
      setNameColumn(value);
    }
  };

  const handleTemplateSelect = (element) => {
    setTemplate(element);
    setTemplateModalDisplay(false);
    setMessageIn('T');
  };

  const getCreditsAndMessages = useCallback(async () => {
    await checkCreditsAndMessages();
  }, [validRows]);

  useEffect(() => {
    getCreditsAndMessages();
  }, [getCreditsAndMessages]);

  const getRowsValidated = useCallback(() => {
    getValidatedRows();
  }, [messageIn, template, messageColumn, numberColumn, nameColumn, totalRows]);

  useEffect(() => {
    getRowsValidated();
  }, [getRowsValidated]);

  const calculateTotalCredits = (validRows) => {
    let creditsCost = 0;
    for (let i = 0; i < validRows.length; i++) {
      const element = validRows[i];
      const smsText =
        messageIn === 'T'
          ? template?.template_text || ''
          : element[messageColumn] || '';
      const messagePartCount = getMessageParts(smsText);

      const internationalRecipient =
        countryCode &&
        element[numberColumn].slice(0, countryCode.length + 1) !==
          '+' + countryCode &&
        element[numberColumn][0] === '+';

      creditsCost += messagePartCount * (internationalRecipient ? 2 : 1);
    }
    return creditsCost;
  };

  const checkCreditsAndMessages = async () => {
    try {
      const { creditRemaining, messagesRemaining } = await updateAccountStatus(
        user
      );

      if (
        (messagesRemaining || messagesRemaining === 0) &&
        messagesRemaining - validRows.length < 0
      ) {
        setHasError(true);
        setErrorText('Not enough messages remaining for this send.');
        return true;
      } else if (
        (creditRemaining || creditRemaining === 0) &&
        creditRemaining - calculateTotalCredits(validRows) < 0
      ) {
        setHasError(true);
        setErrorText('Not enough credits remaining for this send.');
        return true;
      }
      setHasError(false);
      setErrorText('');
      return false;
    } catch (error) {
      console.error(error);
    }
  };

  const sendAction = async () => {
    if (stage === 0) {
      if (await checkCreditsAndMessages()) {
        setStage(3);
        return;
      }
      setStage(1);
      setHasError(false);
      setErrorText('');

      const batchSize = 500;
      let result = true;

      const messagesToSend = validRows.map((row) => ({
        name: row[nameColumn],
        number: stripNonNumerical(row[numberColumn]),
        message: row.templateMessage ? row.templateMessage : row[messageColumn],
      }));

      for (let i = 0; i < Math.ceil(validRows.length / batchSize); i++) {
        const data = {
          messages: messagesToSend.slice(
            i * batchSize,
            i * batchSize + batchSize
          ),
        };

        if (allowScheduling) {
          data.scheduled_time = scheduledTime;
        }

        if (subject) data.subject = subject;

        const singleResult = await sendBulkSMS(data, errorHandler);
        if (result === true) result = singleResult;
      }

      console.log(result);
      if (result === true) setStage(2);
      else setStage(3);
    }
  };

  const parseMessage = (j, smsText, element) => {
    const regText = `[${j}]`;
    return smsText.replace(regText, element[j]);
  };

  const checkForEmptyMessages = () => {
    if (validRows.length === 0) return true;

    if (messageIn === 'T') {
      if (!template?.template_text) return true;
    } else {
      for (let i = 0; i < validRows.length; i++) {
        const singleMessage = validRows[i][messageColumn];
        if (!singleMessage || typeof singleMessage === 'undefined') return true;
      }
    }
  };

  const getValidatedRows = async () => {
    const incrementInvalidRow = [];
    const incrementValidRow = [];

    for (let i = 0; i < totalRows.length; i++) {
      const singleRowNumber = totalRows[i][numberColumn];
      const newRow = [...totalRows[i]];

      if (
        !validatePhoneNumber(stripNonNumerical(singleRowNumber)) ||
        (!totalRows[i][messageColumn] &&
          (messageIn !== 'T' || !template?.template_text)) ||
        !withinCharacterLimit(totalRows[i][messageColumn])
      ) {
        newRow.valid = false;
        incrementInvalidRow.push(newRow);
      } else {
        newRow.valid = true;
        incrementValidRow.push(newRow);
      }
    }

    for (let i = 0; i < incrementValidRow.length; i++) {
      let element = incrementValidRow[i];
      let smsText = element[messageColumn] || '';

      if (messageIn === 'T') smsText = template?.template_text || '';

      for (let j = 0; j < element.length; j++) {
        if (j !== parseInt(messageColumn) || messageIn === 'T') {
          smsText = parseMessage(j, smsText, element);
        }
      }
      if (messageIn === 'T') {
        incrementValidRow[i].templateMessage = smsText;
      } else incrementValidRow[i][messageColumn] = smsText;
    }

    setInvalidRows(incrementInvalidRow);
    setValidRows(incrementValidRow);
  };

  const getFirstMessageValue = () => {
    if (validRows.length > 0) {
      return validRows[0].templateMessage || validRows[0][messageColumn];
    }
  };

  const generateSendModal = () => {
    const msgValue = getFirstMessageValue();

    const stageConfirmation = (
      <Box>
        <Typography fontWeight={700} gutterBottom>
          Are you sure you want to send this message to {validRows.length}{' '}
          recipients?
        </Typography>

        {invalidRows.length > 0 && (
          <>
            <Typography fontWeight={700}>
              {invalidRows.length} recipient&rsquo;s numbers are incorrect or
              empty.
            </Typography>
            <Typography fontWeight={700} gutterBottom>
              Messages can not be delivered to these recipients.
            </Typography>
            <Typography fontWeight={700}>
              Do you want to continue sending?
            </Typography>
            <Typography fontWeight={700} gutterBottom>
              If you answer &lsquo;Yes&rsquo; recipients with incorrect numbers
              will be ignored.
            </Typography>
          </>
        )}

        <Typography>
          Message Preview:
          <br />
          {msgValue}
        </Typography>
      </Box>
    );
    return (
      <StagedModal
        showStaged={sendModalDisplay}
        stageTitle={
          stage === 3
            ? BulkSendFormattedMessages.modalSendFailureTitle
            : BulkSendFormattedMessages.modalSendTitle
        }
        stageConfirmation={stageConfirmation}
        stagePending={BulkSendFormattedMessages.modalSendPending}
        stage={stage}
        stageSuccess={`${validRows.length}/${validRows.length} Message(s) are on the way!`}
        stageFailure={
          hasError ? errorText : BulkSendFormattedMessages.modalSendFailure
        }
        removeStaged={() => {
          setSendModalDisplay(false);
          setHasError(false);
        }}
        removeStagedSlow={() => {
          setStage(0);
        }}
        performAction={sendAction}
      />
    );
  };

  const generateTableBody = () => {
    if (fileSelected) {
      let displayList = validRows;
      if (showRows === 'invalid') displayList = invalidRows;
      else if (showRows === 'all') {
        const combinedList = validRows.concat(invalidRows);
        displayList = combinedList.sort((a, b) => a[0] - b[0]);
      }
      return (
        <Stack>
          <Stack
            sx={{ flexDirection: 'row', alignSelf: 'end', marginY: 2, gap: 2 }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Radio
                    onChange={(e) => handleFilterChange(e)}
                    checked={showRows === 'all'}
                    name="showRows"
                    value="all"
                    id={'showRows_all'}
                    sx={{ padding: 0 }}
                    color="secondary"
                  />
                }
                label={`All Rows: ${totalRowsCount}`}
              />

              <FormControlLabel
                control={
                  <Radio
                    onChange={(e) => handleFilterChange(e)}
                    checked={showRows === 'valid'}
                    name="showRows"
                    value="valid"
                    id={'showRows_valid'}
                    sx={{ padding: 0 }}
                    color="secondary"
                  />
                }
                label={`Valid Rows: ${validRows.length}`}
              />

              <FormControlLabel
                control={
                  <Radio
                    onChange={(e) => handleFilterChange(e)}
                    checked={showRows === 'invalid'}
                    name="showRows"
                    value="invalid"
                    id={'showRows_invalid'}
                    sx={{ padding: 0 }}
                    color={invalidRows.length > 0 ? 'error' : 'secondary'}
                  />
                }
                label={`Invalid Rows: ${invalidRows.length}`}
                sx={{
                  marginBottom: 0,
                  color: invalidRows.length > 0 ? 'error.main' : 'inherit',
                }}
              />
            </FormGroup>
            <Box sx={{ alignSelf: 'end' }}>
              <Button
                disabled={!isDeleting}
                color="secondary"
                variant="outlined"
                onClick={() => handleDeleteRows()}
              >
                {BulkSendFormattedMessages.deleteSelectedRow}
              </Button>
            </Box>
          </Stack>

          <BulkSendTable
            list={displayList}
            numberColumn={numberColumn}
            nameColumn={nameColumn}
            messageColumn={messageColumn}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            changeEvent={(i) => {
              setPageNumber(i);
              setIsDeleting(false);
              setRowsToBeDeleted([]);
              clearCheckboxes('delete');
            }}
            selectRowsToBeDeleted={selectRowsToBeDeleted}
            rowsToBeDeleted={rowsToBeDeleted}
            templateMessage={template.templateMessage}
          />
        </Stack>
      );
    }
  };

  const generateRecipientInSection = () => {
    const options = [
      <MenuItem key="empty" hidden value=""></MenuItem>,
      <MenuItem key="no_numbers" hidden value="no_numbers">
        select
      </MenuItem>,
    ];

    if (headerRow) {
      for (let i = 1; i < headerRow.length; i++) {
        options.push(
          <MenuItem key={`option-${i}`} value={i}>
            {headerRow[i] + ` (Column ${i})`}
          </MenuItem>
        );
      }
    }

    return (
      <Stack sx={{ gap: 2, maxWidth: { lg: 665 } }}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            columnGap: 2,
            alignItems: { md: 'center' },
          }}
        >
          <InputLabel id="numberColumnLabel" sx={{ minWidth: 200 }}>
            {BulkSendFormattedMessages.mobileNumbers}
          </InputLabel>

          <Select
            name="numberColumn"
            id="numberColumn"
            labelId="numberColumnLabel"
            size="small"
            value={numberColumn}
            disabled={!fileSelected}
            onChange={handleInputChange}
            fullWidth
          >
            {options}
          </Select>
        </Stack>

        <Stack
          sx={{
            flexDirection: { md: 'row' },
            columnGap: 2,
            alignItems: { md: 'center' },
          }}
        >
          <InputLabel className="nameColumnLabel" sx={{ minWidth: 200 }}>
            {BulkSendFormattedMessages.recipientNames}
          </InputLabel>

          <Select
            name="nameColumn"
            id="nameColumn"
            labelId="nameColumnLabel"
            size="small"
            value={nameColumn}
            disabled={!fileSelected}
            onChange={handleInputChange}
            fullWidth
          >
            {options}
          </Select>
        </Stack>
      </Stack>
    );
  };

  const generateMessageInSection = () => {
    let templateDisplayText = '';
    let templateDisplayed = false;
    let csvColumnDisplay = messageColumn;
    const options = [
      <MenuItem key="empty" hidden value="empty"></MenuItem>,
      <MenuItem key="no_csv" hidden value="no_csv">
        Please upload a CSV
      </MenuItem>,
    ];

    if (messageIn === 'T') {
      templateDisplayText = template?.template_text
        ? `${template.template_name} (${template.template_text})`
        : 'Please select a template';
      templateDisplayed = true;
      csvColumnDisplay = 'empty';
    } else {
      if (headerRow) {
        for (let i = 1; i < headerRow.length; i++) {
          options.push(
            <MenuItem key={`option-${i}`} value={i}>
              {headerRow[i] + ` (Column ${i})`}
            </MenuItem>
          );
        }
      } else if (!headerRow) {
        csvColumnDisplay = 'no_csv';
      }
    }

    return (
      <Stack sx={{ gap: 2 }}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            columnGap: 2,
            alignItems: { md: 'center' },
            maxWidth: { lg: 665 },
          }}
        >
          <FormLabel sx={{ minWidth: 200, marginBottom: 0 }}>
            <Radio
              name="messageIn"
              value="C"
              onChange={handleInputChange}
              checked={!templateDisplayed}
              id="csv_message_column"
              color="secondary"
            />
            {BulkSendFormattedMessages.csvFileColumn}
          </FormLabel>

          <Select
            value={csvColumnDisplay}
            disabled={templateDisplayed || csvColumnDisplay === 'no_csv'}
            onChange={handleInputChange}
            name="messageColumn"
            id="messageColumn"
            labelId="messageColumnLabel"
            size="small"
            fullWidth
          >
            {options}
          </Select>
        </Stack>

        <Stack
          sx={{
            flexDirection: { md: 'row' },
            columnGap: 2,
            alignItems: { md: 'center' },
            maxWidth: { lg: 800 },
          }}
        >
          <FormLabel sx={{ minWidth: 200, marginBottom: 0 }}>
            <Radio
              name="messageIn"
              value="T"
              onChange={handleInputChange}
              checked={templateDisplayed}
              id="template_message_column"
              color="secondary"
            />
            {BulkSendFormattedMessages.templateRadio}
          </FormLabel>

          <TextField
            type="text"
            name="tempalteMessage"
            value={templateDisplayed ? templateDisplayText : ''}
            disabled
            size="small"
            sx={{ flex: 1 }}
          />

          <Box sx={{ marginTop: { xs: 2, md: 0 } }}>
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => handleModal('templateModalDisplay')}
            >
              {BulkSendFormattedMessages.browseButton}
            </Button>
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Layout
      routes={WebsmsRoutes}
      title={
        openLandingMessage
          ? BulkSendFormattedMessages.bulkSendGuideTitle
          : BulkSendFormattedMessages.bulkSendTitle
      }
    >
      {openLandingMessage ? (
        <BulkSendGuide handleHideMessage={handleHideLandingMessage} />
      ) : (
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <Grid size={{ xs: 12, lg: 9 }}>
            <Grid container sx={{ gap: 2 }}>
              <Grid size={12}>
                <SectionWrapper title="Choose Recipient List" icon="bulkSend">
                  <Stack
                    sx={{
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { md: 'center' },
                      marginBottom: 1,
                      gap: 2,
                      maxWidth: 800,
                    }}
                  >
                    <InputLabel sx={{ marginBottom: 0, minWidth: 130 }}>
                      {BulkSendFormattedMessages.csvFile}
                    </InputLabel>

                    <TextField
                      name="cvsFile"
                      id="cvsFile"
                      placeholder={csvFileObject.name}
                      size="small"
                      fullWidth
                      disabled
                    />

                    <FilePicker
                      extensions={['csv']}
                      onChange={(fileObject) => captureFile(fileObject)}
                      onError={(errMsg) => setErrorMessage(errMsg)}
                    >
                      <Button variant="outlined" color="secondary">
                        {BulkSendFormattedMessages.browseButton}
                      </Button>
                    </FilePicker>
                  </Stack>

                  <Typography
                    fontSize={11}
                    textAlign="center"
                    sx={{
                      maxWidth: 800,
                    }}
                  >
                    <Link
                      tabIndex={0}
                      onClick={() => setOpenLandingMessage(true)}
                      variant="grey"
                    >
                      {BulkSendFormattedMessages.viewGuide}
                    </Link>
                  </Typography>
                </SectionWrapper>
              </Grid>

              <Grid size={12}>
                <SectionWrapper title="Recipient Details" icon="contact">
                  {generateRecipientInSection()}
                </SectionWrapper>
              </Grid>

              <Grid size={12}>
                <SectionWrapper title="Message Details" icon="messages">
                  {generateMessageInSection()}
                </SectionWrapper>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <SectionWrapper title="Schedule Send" icon="schedule">
                  <Stack sx={{ maxWidth: 450 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowScheduling}
                          value={allowScheduling}
                          onChange={(e) => handleInputChange(e)}
                          name="allowScheduling"
                          color="secondary"
                        />
                      }
                      label={BulkSendFormattedMessages.sendLater}
                    />

                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        format="DD/MMM/YYYY h:mm A"
                        defaultValue={
                          !allowScheduling
                            ? moment(new Date())
                            : moment(scheduledTime)
                        }
                        fullWidth
                        onChange={(value) => changeScheduledDateTime(value)}
                        minDate={moment(new Date())}
                        disabled={!allowScheduling}
                        views={['year', 'month', 'day', 'hours', 'minutes']}
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <Stack
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: 1,
                      }}
                    >
                      <Typography fontSize={10}>
                        Users Timezone is {localStorage.getItem('timeString')}
                      </Typography>
                    </Stack>

                    <Stack sx={{ marginTop: 2, marginBottom: 1 }}>
                      <TextField
                        id="contactSearch"
                        label={'(Optional) Add Subject'}
                        variant="outlined"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        fullWidth
                        disabled={!allowScheduling}
                        size="small"
                      />
                    </Stack>
                  </Stack>
                </SectionWrapper>
              </Grid>

              <Grid container size={{ xs: 12, md: 'grow' }}>
                <Stack
                  sx={{
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: theme.palette.secondary.light,
                    flex: 1,
                    paddingTop: 1.5,
                    paddingBottom: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                    <CreditsIcon
                      sx={{ height: 30, width: 34, color: 'primary.main' }}
                    />
                    <MessagesIcon
                      sx={{ height: 30, width: 30, color: 'primary.main' }}
                    />
                  </Stack>
                  <Typography fontSize={24}>{accountStatus}</Typography>

                  <Typography lineHeight={1.2}>
                    {BulkSendFormattedMessages.accountStatus}
                    <br />
                    {BulkSendFormattedMessages.accountStatusFormat}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={12} sx={{ paddingX: 4, paddingY: 0 }}>
                {hasError && (
                  <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {errorText}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleModal('sendModalDisplay')}
                  disabled={
                    !fileSelected ||
                    hasError ||
                    checkForEmptyMessages() ||
                    showInvalidRows
                  }
                >
                  {BulkSendFormattedMessages.startButton}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, lg: 2.5 }}>
            <SideNote message="This is where information about the file format and the template building guide could live." />
          </Grid>
        </Grid>
      )}

      {generateTableBody()}

      <CustomModal
        setOpen={setTemplateModalDisplay}
        open={templateModalDisplay}
        title={BulkSendFormattedMessages.chooseTemplateText}
        onClose={() => handleModal('templateModalDisplay')}
        width={500}
        icon="templates"
      >
        <TemplateList
          handleTemplateSelect={handleTemplateSelect}
          customer={customer}
          errorHandler={errorHandler}
        />
      </CustomModal>
      {generateSendModal()}
    </Layout>
  );
}
